import React, { useState } from 'react';
import { Check, Hash, MessageSquare } from 'lucide-react';
import { cn } from '../../../../../../lib/utils';

interface Channel {
  id: string;
  name: string;
}

interface StepProps {
  result: {
    ui_step: string;
    channels: Channel[];
    connected: boolean;
  };
  onSelect: (value: string, label: string) => void;
}

const SlackChannelSelectorStep: React.FC<StepProps> = ({ result, onSelect }) => {
  const [selectedChannel, setSelectedChannel] = useState<string>('');
  const [search, setSearch] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const filteredChannels = result.channels.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleConfirm = () => {
    if (!selectedChannel || submitted) return;
    setSubmitted(true);
    const channel = result.channels.find((c) => c.id === selectedChannel);
    onSelect(selectedChannel, channel?.name || selectedChannel);
  };

  if (!result.connected) {
    return (
      <div className="w-full max-w-lg p-4 rounded-lg border border-amber-200 bg-amber-50">
        <p className="text-sm text-amber-800">
          Slack is not connected. Please connect Slack in Settings → Connected Apps first.
        </p>
      </div>
    );
  }

  if (submitted) {
    const channel = result.channels.find((c) => c.id === selectedChannel);
    return (
      <div className="w-full max-w-lg p-4 rounded-lg border border-violet-200 bg-violet-50">
        <div className="flex items-center gap-2 text-violet-700">
          <Check className="h-4 w-4" />
          <Hash className="h-3.5 w-3.5" />
          <span className="text-sm font-medium">{channel?.name || selectedChannel}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-lg">
      <p className="text-sm font-medium text-gray-700 mb-3">
        Which Slack channel should notifications be sent to?
      </p>

      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search channels..."
        className="w-full px-3 py-2 mb-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
      />

      <div className="max-h-[200px] overflow-y-auto border border-gray-200 rounded-lg mb-3">
        {filteredChannels.length === 0 ? (
          <p className="px-3 py-4 text-center text-xs text-gray-400">No channels found</p>
        ) : (
          filteredChannels.map((channel) => {
            const isSelected = selectedChannel === channel.id;
            return (
              <button
                key={channel.id}
                type="button"
                onClick={() => setSelectedChannel(channel.id)}
                className={cn(
                  'flex w-full items-center gap-2 px-3 py-2 text-left text-sm border-b border-gray-100 last:border-0 transition-colors',
                  isSelected
                    ? 'bg-violet-50 text-violet-700'
                    : 'hover:bg-gray-50 text-gray-700'
                )}
              >
                <Hash className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                <span className="flex-1 truncate">{channel.name}</span>
                {isSelected && <Check className="h-3.5 w-3.5 text-violet-600" />}
              </button>
            );
          })
        )}
      </div>

      <button
        type="button"
        onClick={handleConfirm}
        disabled={!selectedChannel}
        className={cn(
          'w-full py-2 rounded-md text-sm font-medium transition-colors',
          selectedChannel
            ? 'bg-violet-600 text-white hover:bg-violet-700'
            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
        )}
      >
        Select Channel
      </button>
    </div>
  );
};

export default SlackChannelSelectorStep;
